import React, { useState, useRef, useEffect } from 'react';
import { Send, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface InputAreaProps {
  loading?: boolean;
  onSend: (message: string) => void;
  onNewChat?: () => void;
  onClearChat?: () => void;
  placeholder?: string;
}

export default function InputArea({ 
  loading, 
  onSend, 
  onNewChat,
  onClearChat,
  placeholder = "输入您的问题，例如：我的620分可以报什么学校？"
}: InputAreaProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !loading) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t bg-white dark:bg-gray-800 p-2 md:p-4">
      <div className="max-w-4xl mx-auto space-y-2 md:space-y-3">
        {/* 快捷操作按钮 - 移动端显示在输入框上方 */}
        <div className="flex items-center gap-1 md:gap-2 px-2">
          {onNewChat && (
            <Button
              variant="outline"
              size="sm"
              onClick={onNewChat}
              className="text-xs flex-1 md:flex-none"
            >
              <Plus className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">新对话</span>
              <span className="sm:hidden">新</span>
            </Button>
          )}
          {onClearChat && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearChat}
              className="text-xs flex-1 md:flex-none"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">清空</span>
              <span className="sm:hidden">清</span>
            </Button>
          )}
        </div>

        {/* 输入区域 */}
        <div className="flex gap-1 md:gap-2 items-end px-2">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={loading}
            className="min-h-[48px] md:min-h-[60px] max-h-[150px] md:max-h-[200px] resize-none text-sm md:text-base flex-1"
            rows={1}
          />
          <Button
            onClick={handleSend}
            disabled={!message.trim() || loading}
            size="icon"
            className="h-[48px] w-[48px] md:h-[60px] md:w-[60px] flex-shrink-0"
            aria-label="发送消息"
          >
            <Send className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
        </div>

        {/* 提示文本 - 移动端隐藏 */}
        <p className="text-xs text-gray-400 text-center hidden md:block">
          Enter 发送，Shift + Enter 换行
        </p>
        <p className="text-xs text-gray-400 text-center md:hidden">
          点击发送按钮发送消息
        </p>
      </div>
    </div>
  );
}
